import { useMemo, useState } from "react";
import { format } from "date-fns";

function safeFormat(value: unknown, fmt = "MMM d"): string {
  if (value == null || value === "") return "";
  try {
    const d = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(d.getTime())) return "";
    return format(d, fmt);
  } catch {
    return "";
  }
}
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { TAG_COLORS } from "@/lib/constants";
import type { TrendsBucket } from "@/api/analytics";

interface TrendsChartProps {
  series?: Array<{ key: string; label: string; color?: string | null }>;
  buckets?: TrendsBucket[];
  loading?: boolean;
}

/** Default-on series — keeps the chart legible on first render. */
const DEFAULT_VISIBLE = new Set(["new", "qualified", "won"]);

/** Map a TAG_COLORS key onto a hex stroke / fill. Falls back to a
 *  neutral indigo so unknown stage colors still render something. */
function strokeFor(color: string | null | undefined): string {
  if (!color) return "#6366f1";
  // TAG_COLORS classes carry tailwind utilities, not hex — derive the
  // hex by matching the dot class to a known palette. Cheaper than
  // wiring the CSS resolution; covers every catalog colour.
  const lookup: Record<string, string> = {
    red: "#ef4444",
    orange: "#f97316",
    amber: "#f59e0b",
    yellow: "#eab308",
    lime: "#84cc16",
    green: "#22c55e",
    emerald: "#10b981",
    teal: "#14b8a6",
    cyan: "#06b6d4",
    blue: "#3b82f6",
    indigo: "#6366f1",
    violet: "#8b5cf6",
    purple: "#a855f7",
    fuchsia: "#d946ef",
    pink: "#ec4899",
    rose: "#f43f5e",
    gray: "#94a3b8",
    slate: "#94a3b8",
  };
  return lookup[color] ?? lookup.slate;
}

export function TrendsChart({
  series,
  buckets,
  loading,
}: TrendsChartProps) {
  const safeSeries = series ?? [];
  const safeBuckets = buckets ?? [];
  const [visible, setVisible] = useState<Set<string>>(() => {
    const initial = safeSeries.filter((s) => DEFAULT_VISIBLE.has(s.key));
    return new Set(
      initial.length > 0
        ? initial.map((s) => s.key)
        : safeSeries.map((s) => s.key),
    );
  });

  function toggle(key: string) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Recharts wants flat objects keyed by series — flatten counts into
  // the bucket itself.
  const data = useMemo(
    () =>
      safeBuckets.map((b) => ({
        at: b.at,
        atLabel: safeFormat(b.at),
        ...(b.counts ?? {}),
      })),
    [safeBuckets],
  );

  if (loading) {
    return (
      <div className="h-[260px] animate-pulse rounded-plane bg-slate-100" />
    );
  }
  if (safeBuckets.length === 0) {
    return (
      <p className="text-sm italic text-slate-400">
        Not enough data to draw a trend.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {safeSeries.map((s, idx) => {
          const stroke = strokeFor(s.color);
          const active = visible.has(s.key);
          return (
            <button
              key={s.key ?? `series-${idx}`}
              type="button"
              onClick={() => toggle(s.key)}
              aria-pressed={active}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                active
                  ? "border-slate-300 bg-slate-50 text-slate-800"
                  : "border-slate-200 bg-white text-slate-400 hover:bg-slate-50",
              )}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: active ? stroke : "transparent",
                  border: active ? "none" : `1.5px solid ${stroke}`,
                }}
                aria-hidden
              />
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
            <defs>
              {safeSeries.map((s, idx) => {
                const c = strokeFor(s.color);
                return (
                  <linearGradient
                    key={s.key ?? `grad-${idx}`}
                    id={`trend-${s.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={c} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={c} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="atLabel"
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
              allowDecimals={false}
              width={32}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: 12,
              }}
            />
            {safeSeries
              .filter((s) => visible.has(s.key))
              .map((s, idx) => {
                const c = strokeFor(s.color);
                return (
                  <Area
                    key={s.key ?? `area-${idx}`}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={c}
                    strokeWidth={2}
                    fill={`url(#trend-${s.key})`}
                    isAnimationActive
                    animationBegin={0}
                  />
                );
              })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Touched constants reference so tree-shaking doesn't drop them on a
// build that re-imports without using a value (defensive — TAG_COLORS
// is the canonical color vocabulary, kept in scope for symmetry).
void TAG_COLORS;
